import { useState } from 'react';
import { useAuthQuery } from '@/services/auth';
import { accountStore } from '@/services/auth/store';
import { useMountEffect } from '@/hooks/state/use-mount-effect';
import { deleteCrossAppCacheByPrefix, deleteCrossAppCacheKeys } from '@/utils/cross-app-cache';
import { appEntryGateDestination } from './-app-entry-gate';

/**
 * How long the gate waits for the entry revalidation before acting on the account it already has.
 * A refetch that never settles must not turn "redirected to the right page" into "spinner forever".
 */
const REVALIDATION_TIMEOUT_MS = 5000;

/**
 * Drops the cached account payloads when the stored account is already one a gate acts on, so the
 * revalidation cannot be answered from the very snapshot it exists to replace.
 *
 * `fetchCurrentAccount` and `fetchAccountByEnvironment` read that cache first; its entries are
 * `cacheOnlyOnce` under a 10s TTL, so a fresher fetch elsewhere cannot overwrite a live one. The
 * mutators in this app drop the keys themselves — see the `refresh` branch of `billing.initialize` —
 * but the Journeys bundle cannot: its cross-app cache service exposes no delete. An account reactivated
 * from the legacy `/account_canceled` page can therefore still arrive here as `canceled`.
 *
 * Conditional because a boot-time entry has an empty store and must keep its cache hit: `session` seeds
 * these keys precisely so the first `useAuthQuery` costs no second request.
 */
function bypassAccountCacheForGatedEntry(pathname: string) {
  const stored = accountStore.get();
  if (stored == null || appEntryGateDestination(stored, pathname) == null) {
    return;
  }

  deleteCrossAppCacheKeys('/accounts/current', `/accounts/${stored.id}`);
  deleteCrossAppCacheByPrefix('/account_by_environment');
}

/**
 * Whether this entry into the authenticated tree has revalidated the account, so a gate may act on it.
 *
 * `accountStore` is kept current by the `useAuthQuery` query function, which lives inside the
 * authenticated layout — the thing a gate renders *instead of* when it redirects. An account whose state
 * changed while the user was on a root-level route (`/account_canceled` reactivating back to suspended,
 * say, where no `subscribeToRefreshAuth` listener is mounted either) therefore reaches the gate as a
 * boot-time snapshot, and the redirect that snapshot produces unmounts the only thing that could have
 * corrected it. Revalidating here breaks that loop for every gate branch at once.
 *
 * Three details are load-bearing:
 *
 * - The unsettled state has to hold from the first render, which is why it is local state rather than a
 *   read of the query. For a query that already holds cached data `isFetching` is `false` on the first
 *   render — the observer subscribes in a post-commit effect — so a hold keyed on it (or on
 *   `useIsFetching`) would let the gate redirect before the refetch had started.
 * - `cancelRefetch: false`. The v5 default cancels an in-flight fetch, which here is the authenticated
 *   layout's own mount refetch: the two would stop deduplicating and every entry would cost two requests.
 * - The refetch is only as fresh as the cross-app account cache allows. `fetchCurrentAccount` and
 *   `fetchAccountByEnvironment` read it first, so an entry that a gate already applies to drops those
 *   keys before refetching — see {@link bypassAccountCacheForGatedEntry}.
 *
 * `pathname` is the URL the account arrived on, passed in rather than read off `window` so it is the
 * same one the gate decides with.
 */
export function useAppEntryRevalidation(pathname: string): boolean {
  const { refetch } = useAuthQuery();
  const [hasSettled, setHasSettled] = useState(false);

  useMountEffect(() => {
    let isActive = true;
    const settle = () => {
      if (isActive) {
        setHasSettled(true);
      }
    };

    bypassAccountCacheForGatedEntry(pathname);

    const timeout = setTimeout(settle, REVALIDATION_TIMEOUT_MS);
    // Settle on either outcome, and swallow the rejection: a refetch that fails says nothing new about
    // the account, so the gate should fall back to the decision it would have made anyway. `.finally`
    // would re-throw here and surface as an unhandled rejection.
    void refetch({ cancelRefetch: false }).then(settle, settle);

    return () => {
      isActive = false;
      clearTimeout(timeout);
    };
  });

  return hasSettled;
}
